import React, { useState, useEffect } from 'react';

interface Props {
  onComplete: (score: number) => void;
  bonusEarned: number;
}

// Table items with their correct positions (0-5 grid positions)
const TABLE_ITEMS = [
  { id: 'napkin', emoji: '🧻', name: 'Napkin', correctPos: 0 },
  { id: 'fork', emoji: '🍴', name: 'Fork', correctPos: 1 },
  { id: 'plate', emoji: '🍽️', name: 'Plate', correctPos: 2 },
  { id: 'knife', emoji: '🔪', name: 'Knife', correctPos: 3 },
  { id: 'spoon', emoji: '🥄', name: 'Spoon', correctPos: 4 },
  { id: 'glass', emoji: '🥛', name: 'Glass', correctPos: 5 },
];

// Shuffle array helper
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export function TableSettingGame({ onComplete, bonusEarned }: Props) {
  const [availableItems, setAvailableItems] = useState(() => shuffleArray(TABLE_ITEMS));
  const [placedItems, setPlacedItems] = useState<(typeof TABLE_ITEMS[0] | null)[]>([null, null, null, null, null, null]);
  const [selectedItem, setSelectedItem] = useState<typeof TABLE_ITEMS[0] | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [attempts, setAttempts] = useState(0);

  // Check if puzzle is complete
  useEffect(() => {
    const allPlaced = placedItems.every(item => item !== null);
    if (allPlaced && !isComplete) {
      // Calculate score based on correct placements
      let correct = 0;
      placedItems.forEach((item, index) => {
        if (item && item.correctPos === index) {
          correct++;
        }
      });
      
      const earnedScore = Math.round((correct / 6) * 15); // Max 15 bonus points
      setScore(earnedScore);
      setIsComplete(true);
      setShowResult(true);
      
      // Notify parent after short delay
      setTimeout(() => {
        onComplete(earnedScore);
      }, 1500);
    }
  }, [placedItems, isComplete, onComplete]);

  const handleItemClick = (item: typeof TABLE_ITEMS[0]) => {
    if (isComplete) return;
    setSelectedItem(item);
  };

  const handleSlotClick = (slotIndex: number) => {
    if (isComplete || !selectedItem) return;
    
    // If slot is occupied, swap back to available
    if (placedItems[slotIndex]) {
      setAvailableItems(prev => [...prev, placedItems[slotIndex]!]);
    }
    
    // Place the selected item
    const newPlaced = [...placedItems];
    newPlaced[slotIndex] = selectedItem;
    setPlacedItems(newPlaced);
    
    // Remove from available
    setAvailableItems(prev => prev.filter(i => i.id !== selectedItem.id));
    setSelectedItem(null);
    setAttempts(prev => prev + 1);
  };

  const handleReset = () => {
    setAvailableItems(shuffleArray(TABLE_ITEMS));
    setPlacedItems([null, null, null, null, null, null]);
    setSelectedItem(null);
    setIsComplete(false);
    setShowResult(false);
    setScore(0);
  };

  const slotLabels = ['Left', 'Fork', 'Plate', 'Knife', 'Spoon', 'Glass'];

  return (
    <div style={{
      background: 'linear-gradient(135deg, #8b4513 0%, #654321 100%)',
      border: '4px solid #5d3a1a',
      padding: 20,
      marginTop: 24,
      borderRadius: 12,
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: 16,
      }}>
        <h4 style={{ color: '#fff8dc', margin: 0, fontSize: 16 }}>
          🍽️ Set the Table Mini-Game
        </h4>
        <div style={{ 
          background: '#4caf50', 
          color: 'white', 
          padding: '4px 12px', 
          fontSize: 12,
          fontWeight: 600,
        }}>
          +{bonusEarned} bonus earned
        </div>
      </div>
      
      <p style={{ color: '#ddd', fontSize: 12, marginBottom: 16, textAlign: 'center' }}>
        {isComplete ? (showResult ? `You scored +${score} bonus points!` : 'Checking...') : 
         selectedItem ? `Place the ${selectedItem.name} on the table` : 
         'Tap an item below, then tap a slot to place it'}
      </p>

      {/* Table slots */}
      <div style={{
        background: '#d4a574',
        border: '3px solid #8b4513',
        padding: 12,
        marginBottom: 16,
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: 8,
      }}>
        {placedItems.map((item, index) => (
          <div
            key={index}
            onClick={() => handleSlotClick(index)}
            style={{
              width: '100%',
              aspectRatio: '1',
              background: item 
                ? (isComplete && item.correctPos === index ? '#c8e6c9' : isComplete ? '#ffcdd2' : '#fff8dc')
                : selectedItem ? '#fff59d' : '#f5deb3',
              border: `2px ${selectedItem && !item ? 'dashed' : 'solid'} ${
                item 
                  ? (isComplete && item.correctPos === index ? '#4caf50' : isComplete ? '#f44336' : '#8b4513')
                  : '#a0522d'
              }`,
              borderRadius: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: selectedItem && !isComplete ? 'pointer' : 'default',
              transition: 'all 0.2s',
              transform: selectedItem && !item ? 'scale(1.05)' : 'scale(1)',
            }}
          >
            {item ? (
              <>
                <span style={{ fontSize: 24 }}>{item.emoji}</span>
                {isComplete && (
                  <span style={{ fontSize: 10, marginTop: 2 }}>
                    {item.correctPos === index ? '✓' : '✗'}
                  </span>
                )}
              </>
            ) : (
              <span style={{ fontSize: 10, color: '#a0522d' }}>{slotLabels[index]}</span>
            )}
          </div>
        ))}
      </div>

      {/* Available items */}
      <div style={{
        background: '#f5f5f5',
        border: '2px solid #333',
        padding: 12,
        display: 'flex',
        justifyContent: 'center',
        gap: 8,
        flexWrap: 'wrap',
        minHeight: 60,
      }}>
        {availableItems.length > 0 ? (
          availableItems.map(item => (
            <button
              key={item.id}
              onClick={() => handleItemClick(item)}
              disabled={isComplete}
              style={{
                width: 48,
                height: 48,
                background: selectedItem?.id === item.id ? '#ffeb3b' : 'white',
                border: `3px solid ${selectedItem?.id === item.id ? '#f57c00' : '#333'}`,
                borderRadius: 8,
                fontSize: 24,
                cursor: isComplete ? 'default' : 'pointer',
                transition: 'all 0.2s',
                transform: selectedItem?.id === item.id ? 'scale(1.1)' : 'scale(1)',
              }}
            >
              {item.emoji}
            </button>
          ))
        ) : (
          <span style={{ color: '#666', fontSize: 13 }}>
            {isComplete ? '✨ Table is set!' : 'All items placed!'}
          </span>
        )}
      </div>

      {/* Result overlay */}
      {showResult && (
        <div style={{
          marginTop: 16,
          padding: 16,
          background: score >= 12 ? '#e8f5e9' : score >= 8 ? '#fff8e1' : '#ffebee',
          border: `3px solid ${score >= 12 ? '#4caf50' : score >= 8 ? '#ff9800' : '#f44336'}`,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>
            {score >= 12 ? '🌟 Perfect!' : score >= 8 ? '👍 Good job!' : '😅 Keep practicing!'}
          </div>
          <div style={{ fontSize: 14, color: '#333', marginBottom: 12 }}>
            {score}/15 points • {placedItems.filter((item, idx) => item?.correctPos === idx).length}/6 correct
          </div>
          <button
            onClick={handleReset}
            style={{
              padding: '8px 24px',
              background: '#2196f3',
              color: 'white',
              border: 'none',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            🔄 Play Again (+bonus)
          </button>
        </div>
      )}

      {/* Instructions */}
      {!isComplete && (
        <div style={{ 
          marginTop: 12, 
          fontSize: 11, 
          color: '#ccc', 
          textAlign: 'center' 
        }}>
          💡 Correct placement: Napkin → Fork → Plate → Knife → Spoon → Glass
        </div>
      )}
    </div>
  );
}
